import React, { useState } from 'react';
import { Alert, Button, Form, Input, Popconfirm, Select, Space, Typography, message } from 'antd';
import { CheckCircleOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';
import { isCredentialStoreUnconfigured, isVerifyNotSupported } from '../../utils/apiError';

const { Paragraph, Text } = Typography;

/**
 * Credential form for one provider, driven entirely by the server's field spec
 * (`GET /me/integrations/providers`). Secrets are write-only: inputs always start empty and are
 * cleared after submit; only the server's masked preview / metadata is ever displayed.
 *
 * `existing` is `null` or `{ label, status, maskedPreview?, updatedAt?, lastVerifiedAt? }`.
 * `onSave({ secrets, label })`, `onRemove()` and `onVerify()` are supplied by the caller (self vs on-behalf)
 * and reject with the API error; this component surfaces it.
 */
const ProviderForm = ({ spec, existing, onSave, onRemove, onVerify, webhookUrl, webhookNote }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [storeError, setStoreError] = useState(null);
    const [verifyNotice, setVerifyNotice] = useState(null);

    const fail = (err, fallback) => {
        if (isCredentialStoreUnconfigured(err)) setStoreError(err.message);
        else message.error(err.message || fallback);
    };

    const initial = {};
    for (const f of spec.fields) if (f.defaultValue) initial[f.key] = f.defaultValue;

    const save = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        setStoreError(null);
        setVerifyNotice(null);
        try {
            const secrets = {};
            for (const f of spec.fields) {
                const v = typeof values[f.key] === 'string' ? values[f.key].trim() : values[f.key];
                if (v) secrets[f.key] = v;
            }
            await onSave({ secrets, label: values.displayLabel?.trim() || undefined });
            form.resetFields(); // never keep a secret in form state after submit
        } catch (err) {
            fail(err, 'Could not save credentials.');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setRemoving(true);
        try {
            await onRemove();
        } catch (err) {
            fail(err, 'Could not disconnect.');
        } finally {
            setRemoving(false);
        }
    };

    const verify = async () => {
        setVerifying(true);
        setStoreError(null);
        setVerifyNotice(null);
        try {
            await onVerify();
        } catch (err) {
            if (isVerifyNotSupported(err)) setVerifyNotice({ type: 'info', title: 'Verification not available', text: err.message });
            else if (isCredentialStoreUnconfigured(err)) fail(err, 'Verification failed.');
            else setVerifyNotice({ type: 'error', title: 'Verification failed', text: err.message || 'Verification failed.' });
        } finally {
            setVerifying(false);
        }
    };

    return (
        <div>
            {existing ? (
                <Paragraph>
                    <Text strong>Stored credentials: </Text>
                    <Text code>{existing.maskedPreview || 'hidden'}</Text>
                    <br />
                    <Text type="secondary">
                        {existing.label ? `${existing.label} · ` : ''}
                        Status {existing.status || 'unknown'}
                        {existing.updatedAt ? ` · Updated ${formatDdMmYyyy(existing.updatedAt)}` : ''}
                        {existing.lastVerifiedAt !== undefined ? ` · ${existing.lastVerifiedAt ? `Verified ${formatDdMmYyyy(existing.lastVerifiedAt)}` : 'Never verified'}` : ''}
                    </Text>
                    <br />
                    <Text type="secondary">Secrets are stored encrypted and never shown again. Enter all fields below to replace them.</Text>
                </Paragraph>
            ) : (
                <Paragraph type="secondary">Not connected yet. Enter the credentials below; they are encrypted and never displayed again.</Paragraph>
            )}

            {webhookUrl && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Webhook"
                    description={<span>Register <Text code copyable>{webhookUrl}</Text> in the provider. {webhookNote}</span>}
                />
            )}
            {storeError && <Alert type="error" showIcon style={{ marginBottom: 16 }} message="Credentials cannot be stored" description={storeError} />}
            {verifyNotice && <Alert type={verifyNotice.type} showIcon style={{ marginBottom: 16 }} message={verifyNotice.title} description={verifyNotice.text} />}

            <Form form={form} layout="vertical" autoComplete="off" requiredMark={false} initialValues={initial}>
                <Form.Item name="displayLabel" label="Label (optional)">
                    <Input maxLength={100} autoComplete="off" />
                </Form.Item>
                {spec.fields.map((f) => (
                    <Form.Item
                        key={f.key}
                        name={f.key}
                        label={f.label}
                        extra={f.help}
                        rules={f.required ? [{ required: true, whitespace: true, message: `${f.label} is required.` }] : []}
                    >
                        {f.options ? (
                            <Select options={f.options} />
                        ) : f.secret ? (
                            <Input.Password autoComplete="new-password" placeholder={existing ? 'Enter new value to replace' : ''} />
                        ) : (
                            <Input autoComplete="off" placeholder={existing ? 'Enter new value to replace' : ''} />
                        )}
                    </Form.Item>
                ))}
                <Space wrap>
                    <Button type="primary" onClick={save} icon={<SaveOutlined />} loading={saving}>
                        {existing ? 'Replace credentials' : 'Save credentials'}
                    </Button>
                    {existing && onVerify && (
                        <Button icon={<CheckCircleOutlined />} onClick={verify} loading={verifying}>Verify with provider</Button>
                    )}
                    {existing && (
                        <Popconfirm
                            title={`Disconnect ${spec.displayName}?`}
                            description="The stored secrets are deleted and cannot be recovered."
                            okText="Disconnect"
                            okButtonProps={{ danger: true }}
                            onConfirm={remove}
                        >
                            <Button danger icon={<DeleteOutlined />} loading={removing}>Disconnect</Button>
                        </Popconfirm>
                    )}
                </Space>
            </Form>
        </div>
    );
};

export default ProviderForm;
